import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Upload, Save, Trash2, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Upload logo palestra (stesso flusso di GymSettings).
 * - gymId presente: upload/delete immediato via API
 * - gymId assente (creazione): solo selezione file; il parent carica dopo POST palestra
 */
export default function GymLogoUploadField({
    gymId = null,
    logoFilename = null,
    file: controlledFile = null,
    onFileChange,
    compact = false
}) {
    const { t } = useTranslation();
    const { getAuthenticatedApi } = useAuth();
    const fileInputRef = useRef(null);
    const [logoFile, setLogoFile] = useState(controlledFile);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [localFilename, setLocalFilename] = useState(logoFilename);

    useEffect(() => {
        setLocalFilename(logoFilename);
    }, [logoFilename]);

    useEffect(() => {
        setLogoFile(controlledFile);
    }, [controlledFile]);

    useEffect(() => {
        if (logoFile) return;
        if (localFilename) {
            setLogoPreview(`/api/serve_image.php?filename=${localFilename}&type=logo&v=${Date.now()}`);
        } else {
            setLogoPreview(null);
        }
    }, [localFilename, logoFile]);

    const notifyFile = (file) => {
        setLogoFile(file);
        if (onFileChange) onFileChange(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleFileInput = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
        if (!allowed.includes(file.type)) {
            window.alert(t('admin.gymManager.logoInvalidType'));
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            window.alert(t('admin.gymManager.logoTooLarge'));
            return;
        }
        notifyFile(file);
    };

    const handleCancelPreview = () => {
        notifyFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (localFilename) {
            setLogoPreview(`/api/serve_image.php?filename=${localFilename}&type=logo`);
        } else {
            setLogoPreview(null);
        }
    };

    const handleUpload = async () => {
        if (!gymId || !logoFile) return;
        try {
            setUploading(true);
            const api = getAuthenticatedApi();
            const formData = new FormData();
            formData.append('logo', logoFile);
            const response = await api.post(
                `gym_logo_upload.php?gym_id=${gymId}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (response.data?.filename) {
                setLocalFilename(response.data.filename);
                setLogoFile(null);
                if (onFileChange) onFileChange(null);
                const ts = response.data.timestamp || Date.now();
                setLogoPreview(`/api/serve_image.php?filename=${response.data.filename}&type=logo&v=${ts}`);
            }
        } catch (err) {
            console.error('Logo upload:', err);
            window.alert(err.response?.data?.error || t('admin.gymManager.logoUploadError'));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!gymId || !localFilename) return;
        if (!window.confirm(t('admin.gymManager.logoDeleteConfirm'))) return;
        try {
            setUploading(true);
            const api = getAuthenticatedApi();
            await api.delete(`gym_logo_upload.php?gym_id=${gymId}`);
            setLocalFilename(null);
            setLogoPreview(null);
            notifyFile(null);
        } catch (err) {
            console.error('Logo delete:', err);
            window.alert(err.response?.data?.error || t('admin.gymManager.logoDeleteError'));
        } finally {
            setUploading(false);
        }
    };

    const deferred = !gymId;

    return (
        <div
            className={`rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 ${
                compact ? 'p-4' : 'p-5'
            }`}
        >
            <div className={`flex ${compact ? 'flex-row items-center gap-5' : 'flex-col sm:flex-row sm:items-start gap-5'}`}>
                <div className="shrink-0">
                    {logoPreview ? (
                        <div className="relative">
                            <img
                                src={logoPreview}
                                alt=""
                                className="w-24 h-24 sm:w-28 sm:h-28 object-contain rounded-lg border border-gray-300 dark:border-gray-600 bg-white"
                            />
                            {logoFile && (
                                <button
                                    type="button"
                                    onClick={handleCancelPreview}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                            <Image size={32} className="text-gray-400" />
                        </div>
                    )}
                </div>

                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        {t('admin.gymManager.logoTitle')}
                        <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                            ({t('common.optional')})
                        </span>
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        {deferred
                            ? t('admin.gymManager.logoHintDeferred')
                            : t('admin.gymManager.logoHint')}
                    </p>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml"
                        onChange={handleFileInput}
                        className="hidden"
                    />

                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 disabled:opacity-50"
                        >
                            <Upload size={16} />
                            {t('admin.gymManager.logoChoose')}
                        </button>

                        {!deferred && logoFile && (
                            <button
                                type="button"
                                onClick={handleUpload}
                                disabled={uploading}
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 disabled:opacity-50"
                            >
                                {uploading ? <LoadingSpinner size="small" /> : <Save size={16} />}
                                {t('admin.gymManager.logoUpload')}
                            </button>
                        )}

                        {!deferred && localFilename && !logoFile && (
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={uploading}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 disabled:opacity-50"
                            >
                                <Trash2 size={16} />
                                {t('admin.gymManager.logoDelete')}
                            </button>
                        )}
                    </div>

                    {logoFile && (
                        <p className="mt-2 text-xs text-green-600 dark:text-green-400 truncate">
                            {logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)
                            {deferred ? ` — ${t('admin.gymManager.logoPendingUpload')}` : ''}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export async function uploadGymLogoFile(api, gymId, logoFile) {
    const formData = new FormData();
    formData.append('logo', logoFile);
    return api.post(`gym_logo_upload.php?gym_id=${gymId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
}
